/**
 * The ball-first test. ONE copy, deliberately. (§4.4, §5.3)
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * "Did the defender reach the BALL before the MAN" is the single question that
 * separates a tackle from a foul, and it was answered in TWO places:
 *
 *   - `resolveTackle` (player-controller.ts) — the sim's verdict, which decides
 *     whether a challenge wins the ball or gives away a free kick;
 *   - `ballReachableBeforeMan` (ai/individual.ts) — the AI's pre-check, which
 *     decides whether to commit to a challenge at all.
 *
 * The AI copy carried a comment calling itself "a carbon copy of resolveTackle's
 * ballFirst geometry … keeping this in lockstep is the whole point". It was not
 * in lockstep. It had drifted, and the comment is exactly why nobody looked:
 *
 *     sim: distToBall - BALL_RADIUS <= centreDistMan - PLAYER_CAPSULE_RADIUS*2
 *          ⇒ distToBall <= centreDistMan - 0.49
 *     AI:  distToBall <= centreDistMan - 0.15
 *
 * The AI committed on 0.15 m of ball advantage; the sim demanded 0.49 m. Every
 * challenge in that 0.34 m band was a foul the AI had confidently decided was a
 * tackle. MEASURED at 137 fouls per 90 minutes against a real-football 20-24 —
 * a whistle every 40 seconds — and it is the same geometry a human presses
 * tackle into, which is why winning the ball also felt impossible.
 *
 * So the fix is structural, not a retune: both callers import this predicate,
 * and a future tuning pass physically cannot desynchronise them again.
 *
 * ── The arithmetic, and where the 0.34 m came from ──────────────────────────
 *
 * The two distances were measured from DIFFERENT ORIGINS. `distToBody`
 * subtracted PLAYER_CAPSULE_RADIUS twice — the defender's own radius and the
 * target's — making it a surface-to-surface gap. `distToBall` subtracted only
 * BALL_RADIUS, leaving it centre-to-surface. The defender's own 0.30 m was
 * cancelled on one side of the comparison and not the other, so the sim was
 * over-strict by exactly one capsule radius. That is not a tuning decision
 * anyone made; it is a dropped term.
 *
 * Both gaps below are surface-to-surface, from the defender's own capsule:
 *
 *     ballGap = |defender → ball| - BALL_RADIUS - PLAYER_CAPSULE_RADIUS
 *     manGap  = |defender → man|  - PLAYER_CAPSULE_RADIUS * 2
 *
 * and the verdict is `ballGap <= manGap + TACKLE_BALL_FIRST_TOLERANCE`, i.e.
 * the margin below is non-negative. With the tolerance at zero this reads
 * `distToBall <= centreDistMan - 0.19`; the historical sim rule is recovered
 * exactly at a tolerance of -0.30, which is what makes the sweep in
 * SESSION_LOG.md a measurement against the old behaviour rather than against a
 * number somebody invented.
 *
 * ── Purity ──────────────────────────────────────────────────────────────────
 *
 * A pure read of `WorldState`: no RNG, no clock, no mutation, no allocation
 * (§15.3, §15.7). Safe to call from the AI layer, which runs inside the
 * authoritative tick and is re-simulated during reconciliation (§2.4).
 */

import type { WorldState } from './types.js';
import {
  BALL_RADIUS,
  PLAYER_CAPSULE_RADIUS,
  TACKLE_BALL_FIRST_TOLERANCE,
} from './constants.js';

/** Horizontal distance. Players and the ball are compared on the ground plane. */
function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * How much NEARER the ball is than the man, in metres, both measured as gaps
 * from `defender`'s own capsule surface.
 *
 * Positive ⇒ the ball is genuinely the nearer thing and the challenge is a
 * tackle. Negative ⇒ the defender arrives at the man first, and the tolerance
 * decides how much of that is forgiven.
 *
 * Exported so callers that want to REPORT the margin (tests, the debug overlay,
 * a future "how close was that" telemetry) read the same number the verdict is
 * made from, rather than recomputing it — which is the mistake this whole file
 * is here to prevent.
 */
export function ballFirstMargin(
  state: WorldState,
  defender: number,
  target: number,
): number {
  const p = state.players;
  const dx = p.px[defender]!;
  const dz = p.pz[defender]!;
  const ballGap = distXZ(dx, dz, state.ball.px, state.ball.pz)
    - BALL_RADIUS - PLAYER_CAPSULE_RADIUS;
  const manGap = distXZ(dx, dz, p.px[target]!, p.pz[target]!)
    - PLAYER_CAPSULE_RADIUS * 2;
  return manGap - ballGap;
}

/**
 * Would a challenge by `defender` on `target` reach the BALL before the MAN?
 *
 * THE one predicate. `resolveTackle` uses it to decide foul-or-tackle; the AI
 * uses it to decide whether to commit. They must never disagree about the
 * GEOMETRY — that disagreement is what a whistle every 40 seconds is made of.
 *
 * `requiredExtraMargin` is the one thing a caller may legitimately vary, and it
 * is not a second opinion about where the ball is. The referee judges the
 * challenge at the instant it CONNECTS and so passes 0. The AI is deciding
 * whether to START one, ~75-107 ms before it connects (contact-actions.ts), and
 * has to be far enough ahead of the line to still be on the right side of it
 * when his foot arrives — so it passes AI_TACKLE_COMMIT_MARGIN. One arithmetic,
 * one place, with the difference stated as a quantity instead of hidden in a
 * duplicate.
 */
export function reachesBallBeforeMan(
  state: WorldState,
  defender: number,
  target: number,
  requiredExtraMargin = 0,
): boolean {
  return ballFirstMargin(state, defender, target) + TACKLE_BALL_FIRST_TOLERANCE
    >= requiredExtraMargin;
}
